import React, { memo, useEffect, useMemo, useState } from 'react';
import {
  anchorCompLabel, anchorFirst, annotate, isZoneRow, marketRowsFor, overlayFrom, rowsUnderCap, zoneInParens,
} from '../../utils/lionAutoListings';
import {
  UPDATING_TITLE, awaitingInclude, includedIdSet, isOutsideSelection, matchesSelectionOrIncluded, overrideStateFor, pinnedIdSet,
} from '../../utils/lionAutoOverrides';
import { buildListingColorLookup, groupNameFor, hexToRgba } from '../../utils/groupColors';
import {
  MAX_LADDER, NO_COST_FLOOR_TEXT, floorHeldText, idAtRank, ladderRanks, signedMoney, unresolvedText,
} from '../../utils/lionAutoLadder';
import {
  TIER_TITLES, cleanHoldLive, floorOverHoldText, heldLCell, holdTierOf, liveRanksOf, quantitiesByRank, seatRowsOf,
} from '../../utils/lionAutoHold';
import { formatDateTimeEastern, formatDateEastern } from '../../utils/dates';
import { joinedText, inOtherGroupOnly } from '../../utils/lionAutoGroups';
import {
  SMART_SORT, autoRankIds, isSmartSort, nextSort, sortComparator,
} from '../../utils/lionAutoSort';
import { useTagColors } from '../../hooks/useTagColors';
import {
  REASON_LABELS, money, sectionText, listingLabel, ageText, statusBanners, describeMatchError,
} from './LaMatchResults';
import LaOverrideButton from './LaOverrideButton';
import LaCompOverrideButton from './LaCompOverrideButton';
import LaRankInsert from './LaRankInsert';
import { ANCHOR_OUTLIER_TITLE, anchorOutlierText, outliersDroppedOf } from '../../utils/lionAutoOutliers';
import { STEP_DOWN_L_TITLE, stepDownFlag } from '../../utils/lionAutoNoComp';

// Lion Auto's listing table: Compare's table, with no price inputs. All our
// listings and every market listing, cut down by the map selection with
// Compare's section rule and then Compare's Qty filter, with Compare's
// columns, cells, group-color dots and row tints, sort headers and "Show My
// Listings First". No select column: on Compare it only feeds bulk price
// updates. When a group is on screen (`title` set: the selection is a saved
// group's sections, or the draft being edited) the market rows come from its
// match result (marketRowsFor): the market listings the server put in its
// sections, from the payload it picked the anchor comp from. The page's
// payload is only fetched on load and Refresh comps, so it can lack the
// anchor comp; while the result loads, or is one for other sections (the
// placeholder kept while the next match loads), they come from that payload. The
// result is laid over the rows: the anchor comp is marked in the Section
// cell and leads the market rows under any sort (the whole table with "Show
// My Listings First" off; the row cap never hides it), and with the split
// ladder on so is our cheapest member ("Split anchor": its quantity sets the
// splits comps must sell in). When its zone fallback searched the sections'
// Skybox zone (no comp in the sections) the market rows take in the zone's,
// outside the sections, each with a "Zone" chip, and a zone anchor comp's
// badge says "Anchor comp (zone)". The market listings its rules dropped (or the
// pricer took out by hand) and our listings in its sections
// that its Qty, rows or public-notes filter kept out of the group, or that
// another group holds ("In another group", the holder named in its title),
// are dimmed with the reason. A member that newly joined a saved group (its
// result's `joined`, the last 48 h) carries a "New in group" chip, amber
// while it is not ranked. The overlay never hides a row. Apart from any
// overlay, the Group column (after Qty) names the group taking a listing of
// ours as a member, the group on screen's chip the stronger ("…" until the
// groups' matches are in, so not yet known never reads as no group); sorted
// on, the listings in no group go last. Groups may share sections but not
// listings: the server gives a listing to one group per event. An older
// server could give it to two or more, so such a listing still carries an
// "In N groups" chip naming them (that server's Auto left it alone).
// Price ladder: with a `ranking` the Rank column comes first — while a draft
// is open, clicking one of our member rows ranks it next (clicking a ranked
// row takes it out), and once something is ranked an unranked member's "#"
// (LaRankInsert) inserts it at a rank typed, as the column numbers them:
// that rank and every one above it move up one; a saved group's ranks are
// read-only. With holding on
// (ranking.holdLive) each rank carries a LIVE / HELD tag: the ladder
// answer's own (its rung's tier) when it has the rank, else counted here as
// the server counts it — ranks 1..holdLive live, and later ones too until the
// live ones hold the Live qty's seats (ranking.holdSeats; each rank's
// quantity as the group's match result read it, our listings only before a
// result, never just the rows the filters show) — and a held rank's
// New L cell says its hold price, or its floor when that beat the hold price
// (no what-if is asked for it). With a `ladder`
// answer, New B / New L / Change columns show it per ranked row, with chips
// for what the ladder says about a listing (held at its floor, no cost so no
// floor, unchanged, no price, unranked, another group — an older server's
// inOtherGroups, empty now — below cost). L is
// always Skybox's own number
// (the Vivid price as reported, New L its what-if); nothing here computes a
// price. Our rows come in with the page's L-poller state laid over them
// (lPricePinned / lVerify / lTracked), and the L Price cell reads it as
// Compare's does.
// Overrides (`overrides`: the group on screen's includeIds / excludeIds /
// excludeCompIds): a listing of ours included by hand carries an "Included"
// chip and shows even outside the selected sections; one excluded by hand,
// and a market listing taken out of the comps by hand, come back from the
// server dimmed as "Excluded by you". While a draft is open a By hand
// column (after Rank) has the Include / Exclude button per row of ours
// (LaOverrideButton) and Not a comp / Use as comp per market row
// (LaCompOverrideButton, by its Vivid listing id); an excluded listing can't
// be ranked. A button the match result decides, and every market row's,
// waits while the preview catches up with the draft (isFetching), and so
// does ranking a listing just included. While a draft
// is open its excluded listings show outside its sections too (dimmed, so
// Undo exclude stays in reach), and "Show my other listings" lists all of
// ours outside them, dimmed, to include one; the market is never widened.
// "Hide other groups' listings" (a view preference) drops our rows that a
// group other than the one on screen holds (any group while just browsing),
// once the memberships are known; the market is never hidden.
// Sort (lionAutoSort): a header click sorts on that column, a Shift-click
// adds it as the next column; Smart sort (on when the table opens) is qty
// bands (1, 2-3, 4+) then Row worst to best inside each, for our rows: with
// "Show My Listings First" on, the market under them stays L lowest first
// (off, the whole table follows it). While a draft is open, Auto rank ranks
// our member rows shown (all of them, not just the rows rendered) in the
// table's order, replacing the draft's ranking.

const ROW_CAP = 200;
const QTY_OPTIONS = ['1', '2', '3', '4', '5', '6'];
const NO_SECTIONS = [];
const NO_TAG_COLORS = {};

// Compare's colgroup, without its select column, plus Lion Auto's Group
const COLS = ['section', 'row', 'seats', 'qty', 'group', 'cost', 'bprice', 'lprice', 'updated', 'notes'];

// Sortable headers, Compare's order with Group after Qty; Notes and Tags
// follow, unsortable
const HEADERS = [
  { column: 'section', label: 'Section' },
  { column: 'row', label: 'Row' },
  { column: 'seats', label: 'Seats' },
  { column: 'qty', label: 'Qty' },
  { column: 'group', label: 'Group', title: 'The pricing group taking this listing as a member. A listing is in one group per event: the group that first held it keeps it, unless another group includes it by hand' },
  { column: 'cost', label: 'Cost' },
  { column: 'bprice', label: 'B Price', title: 'Your price in Skybox (B); it changes here only through a price ladder Apply' },
  { column: 'lprice', label: 'L Price', title: 'Vivid price (L) as Skybox reports it' },
  { column: 'updated', label: 'Updated' },
  { column: 'purchased', label: 'Purchased' },
];

// The ladder answer's columns, right after L Price; unsortable
const LADDER_HEADERS = [
  { column: 'newb', label: 'New B', title: "The ladder's B for this rank; nothing is written until Apply" },
  { column: 'newl', label: 'New L', title: "Skybox's what-if: the Vivid price (L) that new B gives" },
  { column: 'change', label: 'Change', title: 'New B minus the current B, and what the ladder says about the listing' },
];
const LADDER_COLS = LADDER_HEADERS.map(h => h.column);
// A market row's Rank / ladder cells, and its By hand cell with no draft
// toggle for it: rendered, always empty
const EMPTY_CELL = {};
const stopClick = (e) => e.stopPropagation();
// The Rank cell's "#" (LaRankInsert)
const INSERT_TITLE = 'Insert at a rank you type: that rank and the ones above it move up one';
// Our cheapest member's badge, only with the split ladder on
const SPLIT_ANCHOR_TITLE = 'Your cheapest listing in this group: with the split ladder on, comps must sell in the splits '
  + 'its quantity calls for. The Anchor comp sets the price.';
// The anchor comp's badge: in the sections, or (the zone fallback) in their zone
const ANCHOR_COMP_TITLE = 'Cheapest matching market listing';
const ZONE_ANCHOR_COMP_TITLE = "Cheapest matching market listing in the group's zone: its sections have none";
// A zone row's "Zone" chip (zoneNames: " (Floor)", zoneInParens): a comp the
// zone fallback used, or (excluded) one it searched and left out
const zoneChipTitle = (zoneNames, excluded) => `In the group's zone${zoneNames}, outside its sections: `
  + `${excluded ? 'searched' : 'used'} because the sections have no comp`;

// Compare's formatCurrency
const formatCurrency = (value) => {
  if (value === null || value === undefined) return '-';
  return '$' + parseFloat(value).toFixed(2);
};

// Compare's Qty filter: any, or n or more tickets
const matchesQty = (qty, filter) => filter === 'any' || qty >= parseInt(filter, 10);

// No header sorted: the default order (L lowest first)
const NO_SORT = [];
const SORT_HINT = 'Shift-click to add a column to the sort';

// Our listings as rows, keyed by inventory id. The market's rows come from
// marketRowsFor; what a group's result says about a row, from overlayFrom /
// annotate (utils/lionAutoListings).
function ownRows(pricing) {
  return (pricing?.myInventory || []).map((l, i) => ({ key: `m:${i}:${l.id}`, own: true, l }));
}

// Lookups from a ladder answer: inventory id → rung, the members it left
// unranked, the two warnings an apply starts a row unchecked for, the
// floor % (for a held rung's tooltip), the hold price (holding on; a
// held rank's New L cell) and the no-comp step-down's clock (noComp: a
// stepped rung's chip)
function ladderViewFrom(ladder) {
  if (!ladder) return null;
  return {
    rungs: new Map((ladder.rungs || []).map(r => [String(r.inventoryId), r])),
    unranked: new Set((ladder.unranked || []).map(String)),
    inOtherGroups: ladder.inOtherGroups || {},
    belowCost: ladder.belowCost || {},
    floorPct: ladder.floor?.pct ?? null,
    holdPrice: ladder.hold?.price ?? null,
    noComp: ladder.noComp ?? null,
  };
}

// What the ladder says about one listing of ours, as chips in its Change
// cell. A rung the no-comp step-down priced (basis 'stepDown') carries a
// "Step down" chip, and its Unchanged chip says why it did not move.
function ladderFlags(id, view) {
  const flags = [];
  const rung = view.rungs.get(id);
  const stepDown = stepDownFlag(rung, view.noComp, Date.now());
  if (rung?.status === 'UNRESOLVED') {
    flags.push({ text: 'No price found', cls: 'la-reason-chip la-chip-bad', title: `${unresolvedText(rung.reason)}, so nothing is proposed` });
  }
  if (rung?.status === 'UNCHANGED') {
    flags.push({ text: 'Unchanged', cls: 'la-reason-chip', title: stepDown ? stepDown.title : 'The ladder lands on the current B' });
  }
  if (stepDown) flags.push({ ...stepDown, cls: 'la-reason-chip' });
  // Held: its target was under the floor, so it is priced at the floor (an
  // ordinary OK / UNCHANGED rung) and the ranks above climb from it. A held
  // rank (holding on) whose floor is above the hold price is priced at the floor.
  if (rung?.floored && rung.tier === 'HELD') {
    flags.push({ text: 'Floor over hold', cls: 'la-reason-chip la-chip-floor', title: floorOverHoldText(rung, view.holdPrice, view.floorPct) });
  } else if (rung?.floored) {
    flags.push({ text: 'Held by floor', cls: 'la-reason-chip la-chip-floor', title: floorHeldText(rung, view.floorPct) });
  }
  if (rung?.floorNote === 'NO_COST') flags.push({ text: 'No cost — no floor', cls: 'la-reason-chip', title: NO_COST_FLOOR_TEXT });
  if (view.unranked.has(id)) flags.push({ text: 'Unranked', cls: 'la-reason-chip', title: 'In the group but not ranked: its price stays as it is' });
  const groups = view.inOtherGroups[id];
  if (Array.isArray(groups) && groups.length > 0) {
    flags.push({
      text: `In group ${groups[0]}${groups.length > 1 ? ` +${groups.length - 1}` : ''}`,
      cls: 'la-groups-chip',
      title: `Also a member of ${groups.join(', ')}; starts unchecked in Apply`,
    });
  }
  if (view.belowCost[id]) flags.push({ text: 'Below cost', cls: 'la-reason-chip la-chip-bad', title: "The new B is below this listing's cost; starts unchecked in Apply" });
  return flags;
}

// Re-render every 15 s so "comps as of … ago" keeps counting
function useNow(intervalMs = 15000) {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), intervalMs);
    return () => clearInterval(t);
  }, [intervalMs]);
  return now;
}

// Its own component so the clock re-renders this text, not the table.
// pricingAgeMs was the payload's age when the server answered (null when
// unknown); add the time since we got the answer. The ladder bar uses it too.
export function AsOf({ pricingAgeMs, dataUpdatedAt }) {
  const now = useNow();
  if (!Number.isFinite(pricingAgeMs)) return '—';
  return ageText(pricingAgeMs + (dataUpdatedAt ? Math.max(0, now - dataUpdatedAt) : 0)) || '—';
}

// A rank's tier with holding on (lionAutoHold): LIVE (the ladder prices it)
// or HELD (at the hold price), after its rank badge; the Apply modal shows
// it too. Nothing for any other value (holding off).
export function TierTag({ tier }) {
  if (tier !== 'LIVE' && tier !== 'HELD') return null;
  return <span className={`la-tier-tag${tier === 'HELD' ? ' held' : ''}`} title={TIER_TITLES[tier]}>{tier}</span>;
}

// Large events carry ~1 200 comps: render 200 rows at a time. The anchor
// comp's row always renders (rowsUnderCap), after the others when past them.
function CappedRows({ rows, render, colSpan }) {
  const [shown, setShown] = useState(ROW_CAP);
  const { visible, hidden } = rowsUnderCap(rows, shown);
  return (
    <>
      {visible.map(render)}
      {hidden > 0 && (
        <tr>
          <td colSpan={colSpan} className="la-more-cell">
            <button type="button" className="btn btn-secondary btn-sm" onClick={() => setShown(s => s + ROW_CAP)}>
              Show {Math.min(ROW_CAP, hidden)} more ({hidden} hidden)
            </button>
          </td>
        </tr>
      )}
    </>
  );
}

// Compare's L Price cell (renderLPrice). The market's L as is. Ours: Vivid's
// L; a pinned one (Skybox's what-if for a B just written) plain while the
// Vivid listing publishes, dotted while the poller checks that Vivid took
// it, ⚠ when the check window passed and Vivid didn't; "…" for a priced row
// with no L yet (the poller on it, or not); "—" for no B (not on Vivid).
function lPriceCell(l, own) {
  if (!own || l.unitPrice > 0) {
    if (!own || !l.lPricePinned) return formatCurrency(l.unitPrice);
    const v = l.lVerify;
    const prevL = v?.previousUnitPrice > 0 ? formatCurrency(v.previousUnitPrice) : 'its old price';
    if (v?.status === 'stale') {
      const mins = Math.max(1, Math.round((Date.now() - v.since) / 60000));
      return (
        <span
          className="lprice-stale"
          title={`Vivid still shows ${prevL} (set ${mins} min ago). Skybox hasn't pushed this price to Vivid - check Skybox, or Refresh comps to re-check.`}
        >
          {formatCurrency(l.unitPrice)}<span className="lprice-warn">⚠</span>
        </span>
      );
    }
    if (v?.status === 'verifying') {
      return (
        <span
          className="lprice-projected"
          title={`Skybox's projected L for ${formatCurrency(l.listPrice)} - Vivid still shows ${prevL}; checking that it updates…`}
        >
          {formatCurrency(l.unitPrice)}
        </span>
      );
    }
    return <span title="Skybox's calculated L price for this B price - Vivid listing still publishing">{formatCurrency(l.unitPrice)}</span>;
  }
  if (l.listPrice > 0) {
    return (
      <span
        className="lprice-pending"
        title={l.lTracked
          ? "Skybox hasn't published the Vivid listing yet - checking automatically"
          : 'No L price from Skybox yet - click Refresh comps to check again'}
      >…</span>
    );
  }
  return <span className="lprice-notlisted" title="Not on Vivid yet - no B price in Skybox">—</span>;
}

// Compare's Tags cell: our listings' tags as chips in their admin colors
function tagChips(tags, tagColors) {
  const list = Array.isArray(tags) ? tags : (tags ? tags.split(' ').filter(Boolean) : []);
  if (list.length === 0) return '-';
  return list.map((tag, i) => {
    const color = tagColors[tag];
    return (
      <span
        key={i}
        className="tag-chip"
        style={color ? { backgroundColor: color, color: '#fff', borderColor: color } : {}}
      >
        {tag}
      </span>
    );
  });
}

// Group cell of one listing: ours, a chip per group taking it (the group on
// screen's stronger; a long name cut, the cell's title has them all), "…"
// while the groups' matches load and it is in none yet, else "-"; the
// market's always "-"
function groupCell(own, names, currentGroup, loading) {
  if (!own) return '-';
  if (names?.length) {
    return names.map((name, i) => (
      <span key={i} className={`la-group-tag${name === currentGroup ? ' on' : ''}`}>{name}</span>
    ));
  }
  return loading ? <span className="la-muted" title="Loading the groups' matches">…</span> : '-';
}

// inGroups: the names of the Lion Auto groups taking this listing of ours
// (undefined for the market): the Group column, and two or more (an older
// server's; a listing is in one group now) get a chip.
// currentGroup: the name the group on screen goes under in them;
// groupsLoading: the groups' matches are not in yet.
// rankCell (Rank column on): { rank, tier, skipped, onClick, title, cellTitle, insert } —
// tier 'LIVE' / 'HELD' with holding on, else null; onClick set when a click
// ranks / unranks the row; cellTitle on the cell itself (a row that can't be
// ranked yet says why); insert ({ max, onSubmit, disabled, title }) when the
// row can go in at a typed rank (its "#"). ladderCell (ladder columns on): { rung, flags,
// holdPrice } for ours, {} for the market. ranked: this
// listing of ours is in the ladder of the group on screen (the "New in
// group" chip's color). overrideCell (By hand column on): { overrides,
// isMember, resultStale } for ours, { overrides, resultStale } for the
// market (its Not a comp), {} with no toggle for the row. included: the group
// on screen includes it by hand. row.outside: ours, shown outside the
// group's sections (dimmed). zoneNames: the group's zones as " (Floor)"
// (zoneInParens), for a zone row's chip; a market row of the zone's
// (isZoneRow: the zone fallback searched it) carries a "Zone" chip.
// anchorLabel / anchorTitle: the anchor comp badge's text and tooltip, from
// the result's anchorComp (a placeholder's payload rows carry no scope).
function ListingRow({
  row, color, groupName, canSeeCost, tagColors, inGroups, currentGroup, groupsLoading = false,
  rankCell, ladderCell, ranked = false, overrideCell, included = false, zoneNames = '', anchorLabel, anchorTitle,
}) {
  const l = row.l;
  const own = row.own;
  const isZone = !own && isZoneRow(l);
  // Ours: the whole row in the section-group color (Compare's my-row tint;
  // green when no color resolves)
  const tint = own && color ? hexToRgba(color, 0.16) : null;
  const style = tint ? { '--row-tint': tint, '--row-tint-hover': hexToRgba(color, 0.26) } : undefined;
  const cls = own ? 'my-row' : `competitor-row${row.isAnchorComp ? ' la-anchor-comp' : ''}`;
  const multi = own && Array.isArray(inGroups) && inGroups.length >= 2 ? inGroups : null;
  const isNew = own && row.joinedAt !== undefined;
  const isIncluded = own && included;
  const marked = !!(row.isAnchor || row.isAnchorComp || row.reason || multi || isNew || isIncluded || isZone);
  const rankable = !!rankCell?.onClick;
  const rung = ladderCell?.rung;
  // A held rank (holding on) has no what-if, so no New L: it says the B it is
  // written at (the hold price, or its floor when that beat the hold price).
  // Nor has a rank the no-comp step-down priced: "—"
  let newL = '';
  if (rung?.tier === 'HELD') {
    const held = heldLCell(rung, ladderCell.holdPrice);
    newL = <span className="la-muted" title={held.title}>{held.text}</span>;
  } else if (rung?.basis === 'stepDown') {
    newL = <span className="la-muted" title={STEP_DOWN_L_TITLE}>—</span>;
  } else if (rung?.newL != null) newL = formatCurrency(rung.newL);
  return (
    <tr
      className={`${cls}${row.reason || row.outside ? ' la-dim' : ''}${rankable ? ' la-rankable' : ''}`}
      style={style}
      onClick={rankCell?.onClick}
      title={rankable ? rankCell.title : undefined}
    >
      {rankCell && (
        // With a "#" a click beside it stays in the cell, never ranks the row last
        <td title={rankCell.cellTitle} onClick={rankCell.insert ? stopClick : undefined}>
          {rankCell.rank !== undefined && <span className="la-rank-badge">{rankCell.rank}</span>}
          <TierTag tier={rankCell.tier} />
          {rankCell.skipped && <span className="la-muted" title={rankCell.title}>skip</span>}
          {rankCell.insert && (
            <LaRankInsert
              label="#"
              max={rankCell.insert.max}
              onSubmit={rankCell.insert.onSubmit}
              disabled={rankCell.insert.disabled}
              title={rankCell.insert.title}
              listingName={listingLabel(l)}
            />
          )}
        </td>
      )}
      {overrideCell && (
        // The row's click ranks: a click anywhere in this cell (a disabled
        // button too) stays here
        <td className="la-override-cell" onClick={stopClick}>
          {overrideCell.overrides && own && (
            <LaOverrideButton
              overrides={overrideCell.overrides}
              inventoryId={l.id}
              isMember={overrideCell.isMember}
              resultStale={overrideCell.resultStale}
              listingName={listingLabel(l)}
            />
          )}
          {overrideCell.overrides && !own && (
            <LaCompOverrideButton
              overrides={overrideCell.overrides}
              vividListingId={l.vividListingId}
              resultStale={overrideCell.resultStale}
              listingName={listingLabel(l)}
            />
          )}
        </td>
      )}
      <td className="la-section-cell">
        {color && <span className="section-group-dot" style={{ backgroundColor: color }} title={groupName || undefined} />}
        <span>{sectionText(l)}</span>
        {marked && (
          <span className="row-flags la-row-marks">
            {row.isAnchor && <span className="la-anchor-badge" title={SPLIT_ANCHOR_TITLE}>Split anchor</span>}
            {row.isAnchorComp && (
              <span className="la-anchor-badge" title={anchorTitle}>{anchorLabel}</span>
            )}
            {isZone && <span className="la-reason-chip la-chip-zone" title={zoneChipTitle(zoneNames, !!row.reason)}>Zone</span>}
            {isNew && (
              <span className={`la-new-chip ${ranked ? 'ok' : 'warn'}`} title={`New in this group: ${joinedText(row.joinedAt, ranked)}`}>
                New in group
              </span>
            )}
            {row.reason && <span className="la-reason-chip" title={row.detail || undefined}>{REASON_LABELS[row.reason] || row.reason}</span>}
            {isIncluded && (
              <span className="la-groups-chip" title="Included by hand: in this group whatever its sections and filters say">Included</span>
            )}
            {multi && <span className="la-groups-chip" title={multi.join(', ')}>In {multi.length} groups</span>}
          </span>
        )}
      </td>
      <td>{l.row || '-'}</td>
      <td>{l.lowSeat && l.highSeat ? `${l.lowSeat}-${l.highSeat}` : l.seatNumbers || '-'}</td>
      <td>{l.quantity}</td>
      <td className="la-group-cell" title={own && inGroups?.length ? inGroups.join(', ') : undefined}>
        {groupCell(own, inGroups, currentGroup, groupsLoading)}
      </td>
      {canSeeCost && <td className="cost-cell">{own ? formatCurrency(l.unitCostAverage) : '-'}</td>}
      <td className="bprice-cell">
        {own ? (l.listPrice ? formatCurrency(l.listPrice) : '-') : <span className="competitor-price">-</span>}
      </td>
      <td className="lprice-cell">{lPriceCell(l, own)}</td>
      {ladderCell && (
        <>
          <td className="la-newb-cell">{rung?.newB != null ? formatCurrency(rung.newB) : ''}</td>
          <td className="lprice-cell">{newL}</td>
          <td className="la-change-cell">
            {rung?.status === 'OK' && <span className={rung.deltaB < 0 ? 'la-down' : 'la-up'}>{signedMoney(rung.deltaB)}</span>}
            {(ladderCell.flags || []).map(f => <span key={f.text} className={f.cls} title={f.title}>{f.text}</span>)}
          </td>
        </>
      )}
      <td className="updated-cell">
        {own && l.lastPriceUpdate ? formatDateTimeEastern(l.lastPriceUpdate, { hour12: true }) : '-'}
      </td>
      <td className="purchased-cell">{own && l.createdDate ? formatDateEastern(l.createdDate) : '-'}</td>
      <td className="notes-cell">{l.publicNotes || '-'}</td>
      <td className="tags-cell">{own ? tagChips(l.tags, tagColors) : '-'}</td>
    </tr>
  );
}

function StatusError({ info, onRetry, prefix }) {
  return (
    <div className={`la-status ${info.retryable ? 'warn' : 'error'}`}>
      {prefix}{info.text}
      {info.details?.length > 0 && (
        <ul className="la-detail-list">{info.details.map((d, i) => <li key={i}>{d}</li>)}</ul>
      )}
      {info.retryable && onRetry && (
        <button type="button" className="btn btn-secondary btn-sm la-status-action" onClick={() => onRetry()}>Retry</button>
      )}
    </div>
  );
}

/**
 * @param {object} props.pricing           useEventPricing data (myInventory, competitorListings, sectionGroups),
 *   our rows carrying the page's L-poller overlay (lPricePinned, lVerify, lTracked)
 * @param {object} [props.chartData]       buildChartData() output: section → zone, for the group colors
 * @param {boolean} [props.canSeeCost]     the user may see our cost (Cost column)
 * @param {{id, name}[]} props.selectedSections  the map selection; empty = whole venue
 * @param {Map<string, string[]>} [props.groupMembership]  our inventory id → names of the groups taking it
 * @param {boolean} [props.groupsLoading]  the groups' match results are not in yet (Group cells say "…")
 * @param {string} [props.currentGroup]    the name the group on screen goes under in groupMembership
 *   (a draft's: draftGroupName); its chip in the Group column is the stronger
 * @param {string} [props.title]           the group on screen; unset while just browsing
 * @param {object} [props.result]          that group's match result (null while loading); its comps and
 *   excluded rows are the table's market rows
 * @param {{ids: number[], holdLive?: number, holdSeats?: number, onToggle: Function|null, onSet: Function|null,
 *   onInsert?: Function|null}} [props.ranking]  that group's
 *   price-ladder order (inventory ids, rank 1 first), its live count (holding on: > 0, ranks after it
 *   are held) and its Live qty (seats; later ranks go live until the live ones hold this many, 0 = off);
 *   onToggle(inventoryId), onSet(inventoryIds) (Auto rank: replace the order) and
 *   onInsert(inventoryId, beforeId) (a typed rank: in just before the listing shown there, null = at
 *   the end) while a draft is open, null = read-only
 * @param {object} [props.ladder]          the ladder answer for exactly that group and criteria
 * @param {number} [props.staleLCount]     our prices Vivid didn't take within the L check window
 * @param {Function} [props.onCheckL]      check those L prices again now
 * @param {{includeIds: number[], excludeIds: number[], excludeCompIds: number[], onToggle: Function|null,
 *   onToggleComp: Function|null}} [props.overrides]
 *   that group's listings included / excluded by hand and market listings taken out of its comps by
 *   hand (Vivid ids); onToggle(inventoryId, 'include'|'exclude') and onToggleComp(vividListingId)
 *   while a draft is open (the By hand column), null = read-only
 * @param {boolean} [props.showAllOwn]     a draft's "Show my other listings": all of ours, those
 *   outside its sections dimmed
 * @param {Function} [props.onShowAllOwnChange]  onShowAllOwnChange(checked); with a draft open the
 *   checkbox shows
 */
function LaListingsTable({
  pricing, chartData = null, canSeeCost = false, selectedSections = NO_SECTIONS, groupMembership = null,
  groupsLoading = false, currentGroup = null, title, result, emptyText, isLoading, isFetching, error, onRetry, dataUpdatedAt,
  ranking = null, ladder = null, staleLCount = 0, onCheckL, overrides = null, showAllOwn = false, onShowAllOwnChange = null,
}) {
  // View preferences, as on Compare; they stay put when the selection changes.
  // The table opens in Smart sort, the order a new group is ranked in (the
  // server ranks one saved with no ranks that way); Clear sort and the
  // headers work from there
  const [qtyFilter, setQtyFilter] = useState('any');
  const [myFirst, setMyFirst] = useState(true);
  const [hideOthers, setHideOthers] = useState(false);
  const [sort, setSort] = useState(SMART_SORT);
  const { data: tagColors = NO_TAG_COLORS } = useTagColors();

  const sectionGroups = pricing?.sectionGroups;
  const colorFor = useMemo(() => buildListingColorLookup(sectionGroups, chartData), [sectionGroups, chartData]);

  const ownBase = useMemo(() => ownRows(pricing), [pricing]);
  // The market: the group on screen's own match rows, else the payload's in the selection
  const marketBase = useMemo(
    () => marketRowsFor({ pricing, result, selectedSections }),
    [pricing, result, selectedSections]
  );
  // Ours included by hand by the group on screen show wherever they sit, and
  // while a draft is open so do the ones it excludes by hand and, with "Show
  // my other listings", all of ours; those outside its sections are dimmed
  const editingDraft = !!overrides?.onToggle;
  const showOthers = editingDraft && showAllOwn;
  const includedIds = useMemo(() => (overrides ? includedIdSet(overrides) : null), [overrides]);
  const pinnedIds = useMemo(() => (overrides ? pinnedIdSet(overrides, !!overrides.onToggle) : null), [overrides]);
  const visible = useMemo(() => {
    const keep = (r) => matchesSelectionOrIncluded(r.l, r.own, selectedSections, pinnedIds, showOthers)
      && matchesQty(r.l.quantity, qtyFilter);
    // Only ours, and not while the memberships are still loading (unknown is not "no group")
    const hideRow = (r) => hideOthers && !groupsLoading && inOtherGroupOnly(groupMembership?.get(String(r.l.id)), currentGroup);
    const mark = (r) => (isOutsideSelection(r.l, r.own, selectedSections, includedIds) ? { ...r, outside: true } : r);
    // The market is in the selection already (marketRowsFor): only the Qty filter
    return {
      own: ownBase.filter(r => keep(r) && !hideRow(r)).map(mark),
      market: marketBase.filter(r => matchesQty(r.l.quantity, qtyFilter)),
    };
  }, [ownBase, marketBase, selectedSections, pinnedIds, includedIds, showOthers, qtyFilter, hideOthers, groupsLoading, groupMembership, currentGroup]);
  const overlay = useMemo(() => overlayFrom(result), [result]);
  // Only a Group sort reads the membership, so only it re-sorts when that changes
  const sortGroups = sort.some(s => s.column === 'group') ? groupMembership : null;
  const rows = useMemo(() => {
    const groupsOf = (r) => (r.own && sortGroups ? sortGroups.get(String(r.l.id)) : undefined);
    const order = sortComparator(sort, { groupsOf });
    // map() makes fresh arrays, so sorting never touches the memoized lists.
    // The anchor comp leads the market, and the whole table without "Show My
    // Listings First". Smart sort is how we rank OUR listings; pricers read
    // the comps cheapest first, so with ours shown first the market under
    // them keeps the default order (L lowest first). Mixed in, it all sorts.
    const marketOrder = myFirst && isSmartSort(sort) ? sortComparator(NO_SORT) : order;
    const own = visible.own.map(r => annotate(r, overlay)).sort(order);
    const market = visible.market.map(r => annotate(r, overlay)).sort(marketOrder);
    return myFirst ? [...own, ...anchorFirst(market)] : anchorFirst([...own, ...market].sort(order));
  }, [visible, overlay, sort, myFirst, sortGroups]);

  // Ranks as the server will number them: the saved order, members only
  // (unknown until the group's result is in, then every saved id counts)
  const memberIds = useMemo(() => (overlay ? new Set(overlay.ranks.keys()) : null), [overlay]);
  const rankById = useMemo(() => (ranking ? ladderRanks(ranking.ids, memberIds) : null), [ranking, memberIds]);
  // How many of those ranks are live with holding on, as the server counts
  // them (liveRanksOf; 0 with holding off): each rank's seats as the group's
  // match result read them (the read the ranks come from; seatRowsOf), all our
  // listings only before a result, never just the rows the filters show
  const myInventory = pricing?.myInventory;
  const liveRanks = useMemo(
    () => (ranking ? liveRanksOf(ranking.holdLive, ranking.holdSeats, quantitiesByRank(rankById, seatRowsOf(result, myInventory))) : 0),
    [ranking, rankById, result, myInventory]
  );
  const rankedIds = useMemo(() => new Set((ranking?.ids || []).map(String)), [ranking]);
  const ladderView = useMemo(() => ladderViewFrom(ladder), [ladder]);
  // The result on screen is behind the criteria: the preview is catching up
  // with the draft (or its first answer is still loading)
  const resultStale = !!isFetching || (!overlay && !!isLoading && !error);

  // Rank cell of one row. While a draft is open a click on one of our member
  // rows ranks it next; a click on a ranked row takes it out (also when it is
  // no member now, so it can be cleared). Market rows and our rows the
  // group's filters keep out, another group holds, or the pricer excluded
  // by hand (also before the preview catches up) can't be ranked; one just
  // included can once the preview has it, and its cell says so meanwhile.
  // A row a click would rank can also go in at a typed rank once something
  // is ranked (its "#"): the rank as this column numbers it is carried as
  // the listing shown there (idAtRank), max = the end. It waits, disabled,
  // while the result is behind the draft.
  const rankCellFor = (r) => {
    if (!r.own) return EMPTY_CELL;
    const id = String(r.l.id);
    const rank = rankById.get(id);
    const ranked = rankedIds.has(id);
    const skipped = ranked && rank === undefined;
    const excluded = overrideStateFor(overrides, { id }) === 'excluded';
    const isMember = memberIds ? memberIds.has(id) : null;
    const canAdd = !ranked && !excluded && isMember === true && ranking.ids.length < MAX_LADDER;
    const waiting = !!ranking.onToggle && !ranked && awaitingInclude(overrides, { id, isMember, resultStale });
    let title;
    if (skipped) title = 'Ranked, but not in the group now (sold, kept out by its filters, or held by another group): skipped, the ranks after it close up';
    else if (ranked) title = 'Click to take this listing out of the price ladder (the ranks after it close up)';
    else if (canAdd) title = `Click to make this rank ${rankById.size + 1}`;
    const onClick = ranking.onToggle && (ranked || canAdd) ? () => ranking.onToggle(r.l.id) : undefined;
    // A ranked row's tag: the ladder answer's tier when it priced this rank
    // (the server's own count), else the count above
    const tier = rank === undefined ? null : (ladderView?.rungs.get(id)?.tier ?? holdTierOf(rank, liveRanks));
    const insert = ranking.onInsert && canAdd && rankById.size > 0
      ? {
        max: rankById.size + 1,
        onSubmit: (n) => ranking.onInsert(r.l.id, idAtRank(rankById, n)),
        disabled: resultStale,
        title: resultStale ? UPDATING_TITLE : INSERT_TITLE,
      }
      : null;
    // A cell with "#" keeps its clicks (stopClick), so it says so instead of the row's "Click to make this rank N"
    let cellTitle;
    if (waiting) cellTitle = UPDATING_TITLE;
    else if (insert) cellTitle = `${INSERT_TITLE} (click "#"; a click elsewhere on the row makes it rank ${rankById.size + 1})`;
    return { rank, tier, skipped, onClick, title, cellTitle, insert };
  };

  // Auto rank (a draft only): our member rows the table shows, not excluded
  // by hand, in its order — all of them, not just the rows CappedRows renders
  const autoRank = ranking?.onSet
    ? autoRankIds(rows, { memberIds, isExcluded: (id) => overrideStateFor(overrides, { id }) === 'excluded' })
    : null;
  let autoRankTitle = null;
  if (autoRank && resultStale) autoRankTitle = UPDATING_TITLE;
  else if (autoRank && !autoRank.length) {
    autoRankTitle = 'None of the listings shown can be ranked: only your listings in this group, not excluded by hand';
  } else if (autoRank) {
    autoRankTitle = `Rank the ${autoRank.length} listing${autoRank.length === 1 ? '' : 's'} of yours shown in this group in the table's order`
      + ` (top row = rank 1, the lowest price${autoRank.length >= MAX_LADDER ? `; ${MAX_LADDER} at most` : ''}).`
      + ' Replaces the current ranking.';
  }

  // A header click sorts on that column; Shift-click adds it to the sort
  const handleSort = (column, e) => setSort(prev => nextSort(prev, column, !!e?.shiftKey));
  // ↑ / ↓ on a sorted header, and its place once two or more columns sort
  const sortMark = (column) => {
    const at = sort.findIndex(s => s.column === column);
    if (at < 0) return ' ↕';
    const arrow = sort[at].direction === 'asc' ? ' ↑' : ' ↓';
    return sort.length > 1 ? <>{arrow}<sup className="la-sort-priority">{at + 1}</sup></> : arrow;
  };
  const smartOn = isSmartSort(sort);
  const handleSmartSort = () => setSort(smartOn ? NO_SORT : SMART_SORT);

  // A draft always has the Rank column (it is where ranking happens); a
  // saved group only once it has a ranking
  const rankCol = !!ranking && (!!ranking.onToggle || ranking.ids.length > 0);
  // With holding on the Rank column also carries each rank's tier tag
  const tierCol = rankCol && cleanHoldLive(ranking.holdLive) > 0;
  // The By hand column (after Rank): only while a draft is open. A market
  // row's cell, the same for every one: Not a comp when the draft can toggle it
  const overrideCol = editingDraft;
  const marketOverrideCell = overrideCol && overrides.onToggleComp ? { overrides, resultStale } : EMPTY_CELL;
  const ladderCols = !!ladderView;
  const baseCols = canSeeCost ? COLS : COLS.filter(c => c !== 'cost');
  const headers = canSeeCost ? HEADERS : HEADERS.filter(h => h.column !== 'cost');
  // The ladder's columns follow L Price
  const lCol = baseCols.indexOf('lprice') + 1;
  const lHead = headers.findIndex(h => h.column === 'lprice') + 1;
  const cols = [
    ...(rankCol ? [tierCol ? 'rank-tier' : 'rank'] : []),
    ...(overrideCol ? ['override'] : []),
    ...baseCols.slice(0, lCol),
    ...(ladderCols ? LADDER_COLS : []),
    ...baseCols.slice(lCol),
  ];
  // Rank, By hand, the sortable headers with the ladder's, then Notes and Tags
  const columnCount = (rankCol ? 1 : 0) + (overrideCol ? 1 : 0) + headers.length
    + (ladderCols ? LADDER_HEADERS.length : 0) + 2;
  const sortHeader = (h) => (
    <th key={h.column} className="sortable" title={h.title ? `${h.title}\n${SORT_HINT}` : SORT_HINT} onClick={(e) => handleSort(h.column, e)}>
      {h.label}{sortMark(h.column)}
    </th>
  );

  const n = selectedSections.length;
  const banners = result ? statusBanners(result) : [];
  const totals = result?.totals || {};
  // "2nd cheapest · 1 low outlier dropped" when the low-outlier rule dropped cheaper comps
  const anchorNote = result?.anchorComp ? anchorOutlierText(outliersDroppedOf(result), result.anchorComp.scope) : null;
  // The group's zones, " (Floor)", for a zone row's chip
  const zoneNames = zoneInParens(result?.resolved);
  // The anchor comp's name for the head and its row's badge: the result says
  // whether it came from the zone (a placeholder's payload rows carry no scope)
  const anchorLabel = anchorCompLabel(result?.anchorComp);
  const anchorTitle = isZoneRow(result?.anchorComp) ? ZONE_ANCHOR_COMP_TITLE : ANCHOR_COMP_TITLE;

  return (
    <div className={`compare-table-section la-results${isFetching ? ' la-stale' : ''}`}>
      <div className="la-panel-head">
        <h3>{title || 'Listings'}</h3>
        <span className="la-muted">{n ? `${n} section${n === 1 ? '' : 's'}` : 'Whole venue'}</span>
        {result?.anchorComp && (
          <span className="la-muted">
            · {anchorLabel} {listingLabel(result.anchorComp)} ×{result.anchorComp.quantity ?? '?'} at{' '}
            <strong className="la-gold">{money(result.anchorComp.unitPrice)} L</strong>
            {anchorNote && <span title={ANCHOR_OUTLIER_TITLE}> · {anchorNote}</span>}
          </span>
        )}
      </div>

      {title && !result && (error
        ? <StatusError info={describeMatchError(error)} onRetry={onRetry} />
        : (isLoading || emptyText) && <div className="la-status">{isLoading ? 'Matching comps…' : emptyText}</div>)}
      {/* A refetch that failed after an earlier success keeps the old
          overlay on screen, but says it is old */}
      {result && error && <StatusError info={describeMatchError(error)} onRetry={onRetry} prefix="Showing the last result — " />}
      {banners.map(b => (
        <div key={b.code} className={`la-status ${b.kind}`}>{b.text}</div>
      ))}

      <div className="compare-stats-bar">
        <span className="stat-badge mine">Your Listings: {visible.own.length}</span>
        <span className="stat-badge competitor">Competitors: {visible.market.length}</span>
        <div className="filter-group">
          <label>Qty:</label>
          <select value={qtyFilter} onChange={(e) => setQtyFilter(e.target.value)}>
            <option value="any">Any</option>
            {QTY_OPTIONS.map(q => <option key={q} value={q}>{q}+</option>)}
          </select>
        </div>
        <label className="checkbox-filter">
          <input type="checkbox" checked={myFirst} onChange={(e) => setMyFirst(e.target.checked)} />
          Show My Listings First
        </label>
        <label
          className="checkbox-filter"
          title={currentGroup
            ? `Hide your listings that another group holds (not ${currentGroup}). The market is not touched.`
            : 'Hide your listings that any group holds, leaving the ones in no group. The market is not touched.'}
        >
          <input type="checkbox" checked={hideOthers} onChange={(e) => setHideOthers(e.target.checked)} />
          Hide other groups' listings
        </label>
        <button
          type="button"
          className={`btn btn-sm ${smartOn ? 'btn-primary' : 'btn-secondary'}`}
          aria-pressed={smartOn}
          onClick={handleSmartSort}
          title={smartOn
            ? `Smart sort is on${myFirst ? ' for your listings (the market under them stays L lowest first)' : ''}: click to go back to the default order`
            : 'Sort by Qty in bands (1s first, then 2s and 3s together, then 4+ together), then Row from the worst (back rows) to the best inside each band. Click the Row header to flip the rows.'}
        >
          Smart sort
        </button>
        {sort.length > 0 && (
          <button type="button" className="la-link" onClick={() => setSort(NO_SORT)} title="Back to the default order: L lowest first">
            Clear sort
          </button>
        )}
        {autoRank && (
          <button
            type="button"
            className="btn btn-secondary btn-sm"
            disabled={resultStale || autoRank.length === 0}
            onClick={() => ranking.onSet(autoRank)}
            title={autoRankTitle}
          >
            Auto rank {autoRank.length}
          </button>
        )}
        {editingDraft && onShowAllOwnChange && (
          <label
            className="checkbox-filter"
            title="Also list your listings outside this group's sections (dimmed), to include one by hand. The market stays in the sections."
          >
            <input type="checkbox" checked={showAllOwn} onChange={(e) => onShowAllOwnChange(e.target.checked)} />
            Show my other listings
          </label>
        )}
        {staleLCount > 0 && onCheckL && (
          <button
            type="button"
            className="stat-badge warn"
            onClick={onCheckL}
            title="Skybox hasn't pushed these prices to Vivid within ~17 minutes. Click to re-check."
          >
            ⚠ {staleLCount} price{staleLCount === 1 ? '' : 's'} not on Vivid yet
          </button>
        )}
        {result && (
          <span className="la-muted">
            Group: {totals.matched ?? (result.comps || []).length} match
            · {totals.excluded ?? (result.excluded || []).length} excluded
            · comps as of <AsOf pricingAgeMs={result.pricingAgeMs} dataUpdatedAt={dataUpdatedAt} />
            {isFetching && <> · updating…</>}
          </span>
        )}
      </div>

      <div className="compare-table-container">
        <table className="compare-table">
          <colgroup>
            {cols.map(c => <col key={c} className={`col-${c}`} />)}
          </colgroup>
          <thead>
            <tr>
              {rankCol && (
                <th
                  title={ranking.onToggle
                    ? `Price ladder order: click your listings in the order you want, 1 = lowest price${ranking.onInsert ? ' · # inserts at a rank' : ''}`
                    : 'Price ladder order, 1 = lowest price'}
                >
                  Rank
                </th>
              )}
              {overrideCol && (
                <th title="Include or exclude one of your listings by hand, whatever the group's sections and filters say; on a market listing, Not a comp takes it out of the group's comps">By hand</th>
              )}
              {headers.slice(0, lHead).map(sortHeader)}
              {ladderCols && LADDER_HEADERS.map(h => <th key={h.column} title={h.title}>{h.label}</th>)}
              {headers.slice(lHead).map(sortHeader)}
              <th>Notes</th>
              <th>Tags</th>
            </tr>
          </thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={columnCount} className="no-data">No listings found</td></tr>
            ) : (
              <CappedRows
                rows={rows}
                colSpan={columnCount}
                render={(r) => {
                  const id = r.own ? String(r.l.id) : null;
                  return (
                    <ListingRow
                      key={r.key}
                      row={r}
                      color={colorFor(r.l)}
                      groupName={groupNameFor(sectionGroups, r.l)}
                      canSeeCost={canSeeCost}
                      tagColors={tagColors}
                      inGroups={id && groupMembership ? groupMembership.get(id) : undefined}
                      currentGroup={currentGroup}
                      groupsLoading={groupsLoading}
                      rankCell={rankCol ? rankCellFor(r) : undefined}
                      ladderCell={ladderCols
                        ? (id ? { rung: ladderView.rungs.get(id), flags: ladderFlags(id, ladderView), holdPrice: ladderView.holdPrice } : EMPTY_CELL)
                        : undefined}
                      ranked={!!id && rankedIds.has(id)}
                      overrideCell={overrideCol ? (id ? { overrides, isMember: memberIds ? memberIds.has(id) : null, resultStale } : marketOverrideCell) : undefined}
                      included={!!id && !!includedIds?.has(id)}
                      zoneNames={zoneNames}
                      anchorLabel={anchorLabel}
                      anchorTitle={anchorTitle}
                    />
                  );
                }}
              />
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Memoized: typing a group name re-renders the page, and a big event's
// table (hundreds of rows) must not re-render with it. Callers pass stable
// props (query data with the memoized L overlay, query.refetch, the memoized
// selection, chart data, group membership, the memoized ranking and
// overrides, the ladder answer and the poller's stable pollNow).
export default memo(LaListingsTable);
